import { useQuery } from '@tanstack/react-query';

import { getBasket } from '../../../api/basketService';
import { getBasketFromStorage } from '../../../utils/local-storage-versioning';
import { ErrorMessage } from '../../../shared/enum/ErrorMessage';
import { calculateBookingDetails } from '../../../api/bookingService';
import { UserChoices } from '../../../shared/types/user-choices';
import { InstalmentType } from '../../../shared/enum/InstalmentType';
import { queryRetry } from '../../../lib/query-retry';

export const useGetProcessingData = () =>
    useQuery({
        queryKey: ['get-processing-data'],
        queryFn: async () => {
            const storedBasket = getBasketFromStorage();
            const basketId = storedBasket?.id;

            if (!basketId) {
                throw new Error(ErrorMessage.NO_BOOKING_DETAILS_PROVIDED);
            }

            const basket = await getBasket(basketId as string);

            const {
                products: [basketRoom],
            } = basket;

            const { room, building, bookingDetails } = await calculateBookingDetails({
                roomName: basketRoom.room_code,
                academicYear: basketRoom.academic_year,
                ...(basketRoom.stl && { paymentPlanId: basketRoom.payment_plan_id }),
            });

            const foundTermAndOption = bookingDetails.terms
                .map((term) => ({
                    term,
                    instalment: Object.values(term.instalments).find(
                        (instalment) => instalment.id === basketRoom.payment_plan_id
                    ),
                }))
                .find((x) => Boolean(x.instalment));

            if (!foundTermAndOption?.instalment)
                throw new Error(ErrorMessage.NO_USER_CHOICES_PROVIDED);

            const userChoices: UserChoices = {
                numberOfStudents: basketRoom.selected_students,
                instalmentId: basketRoom.payment_plan_id,
                instalmentType: basketRoom.instalment_type as InstalmentType,
                term: foundTermAndOption.term,
            };

            return { room, building, bookingDetails, userChoices, basket };
        },
        retry: queryRetry,
    });
